import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export interface Budget {
  id: string;
  workspace_id: string;
  entity_id: string | null;
  fiscal_year: number;
  period_month: number | null;
  gl_account_code: string | null;
  department: string | null;
  amount_cents: string;
  currency: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetUtilization extends Budget {
  actualCents: number;
  utilizationPct: number | null;
  remainingCents: number;
  overBudget: boolean;
  windowFrom: string;
  windowTo: string;
}

export interface BudgetImpact {
  budgetId: string;
  budgetCents: number;
  actualCents: number;
  projectedCents: number;
  currentUtilizationPct: number | null;
  projectedUtilizationPct: number | null;
  overBudget: boolean;
  currency: string;
  windowFrom: string;
  windowTo: string;
}

export interface BudgetInput {
  entityId?: string | null;
  fiscalYear: number;
  periodMonth?: number | null;
  glAccountCode?: string | null;
  department?: string | null;
  amountCents: number;
  currency?: string;
  note?: string | null;
}

export function useBudgetUtilization(wsId: string | undefined, fiscalYear: number) {
  return useQuery<BudgetUtilization[]>({
    queryKey: ['budget-utilization', wsId, fiscalYear],
    enabled: !!wsId && !!fiscalYear,
    queryFn: async () => {
      const r = await api.get<BudgetUtilization[]>(
        `/workspaces/${wsId}/budgets/utilization?fiscalYear=${fiscalYear}`,
      );
      return r.data;
    },
  });
}

export function useCreateBudget(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: BudgetInput) => {
      const r = await api.post<Budget>(`/workspaces/${wsId}/budgets`, body);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budget-utilization', wsId] });
    },
  });
}

export function useUpdateBudget(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<BudgetInput> }) => {
      const r = await api.patch<Budget>(`/workspaces/${wsId}/budgets/${id}`, patch);
      return r.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budget-utilization', wsId] });
    },
  });
}

export function useDeleteBudget(wsId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/workspaces/${wsId}/budgets/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['budget-utilization', wsId] });
    },
  });
}

/**
 * Pre-approval budget impact for an amount on a GL account at a date. Returns
 * null when no budget covers the period — the caller treats that as "no
 * budget set" and shows nothing.
 */
export function useBudgetImpact(
  wsId: string | undefined,
  args: { glAccountCode?: string | null; department?: string | null; amountCents?: number | null; onDate?: string | null },
) {
  const { glAccountCode, department, amountCents, onDate } = args;
  const enabled = !!wsId && !!glAccountCode && !!amountCents && !!onDate;
  return useQuery<BudgetImpact | null>({
    queryKey: ['budget-impact', wsId, glAccountCode, department, amountCents, onDate],
    enabled,
    queryFn: async () => {
      const params = new URLSearchParams({
        glAccountCode: String(glAccountCode),
        amountCents: String(amountCents),
        onDate: String(onDate),
      });
      if (department) params.set('department', department);
      const r = await api.get<BudgetImpact | null>(
        `/workspaces/${wsId}/budgets/impact?${params.toString()}`,
      );
      return r.data;
    },
  });
}
